import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: 'sales',
    minPriceList: 1,
    isActive: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, priceListsRes] = await Promise.all([
        api.get('/users'),
        api.get('/price-lists')
      ]);
      setUsers(usersRes.data);
      setPriceLists(priceListsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Don't send password if empty (not changing)
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await api.put(`/users/${editingUser._id}`, updateData);
      } else {
        await api.post('/users', formData);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        name: '',
        role: 'sales',
        minPriceList: 1,
        isActive: true
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בשמירה');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      name: user.name,
      role: user.role,
      minPriceList: user.minPriceList || 1,
      isActive: user.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם למחוק משתמש זה?')) {
      try {
        await api.delete(`/users/${id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.message || 'שגיאה במחיקה');
      }
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user._id}`, { isActive: !user.isActive });
      fetchData();
    } catch (error) {
      alert('שגיאה בעדכון');
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'admin': return 'מנהל מערכת';
      case 'editor': return 'עורך';
      case 'sales': return 'איש מכירות';
      default: return role;
    }
  };

  const getRoleClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'editor': return 'role-editor';
      case 'sales': return 'role-sales';
      default: return '';
    }
  };

  if (loading) {
    return <div className="loading">טוען...</div>;
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <h1>ניהול משתמשים</h1>
        <button className="btn-primary" onClick={() => {
          setEditingUser(null);
          setFormData({
            username: '',
            password: '',
            name: '',
            role: 'sales',
            minPriceList: 1,
            isActive: true
          });
          setShowModal(true);
        }}>
          + משתמש חדש
        </button>
      </div>

      <div className="users-grid">
        {users.map(user => (
          <div key={user._id} className={`user-card ${!user.isActive ? 'inactive' : ''}`}>
            <div className="user-header">
              <div className="user-avatar">
                {user.name?.charAt(0) || user.username?.charAt(0)}
              </div>
              <div className="user-info">
                <h3>{user.name}</h3>
                <span className="username">@{user.username}</span>
              </div>
              <span className={`role-badge ${getRoleClass(user.role)}`}>
                {getRoleName(user.role)}
              </span>
            </div>
            
            <div className="user-details">
              {user.role === 'sales' && (
                <div className="detail-row">
                  <span>מחירון מינימלי:</span>
                  <strong>מחירון {user.minPriceList || 1}</strong>
                </div>
              )}
              <div className="detail-row">
                <span>סטטוס:</span>
                <strong className={user.isActive ? 'status-active' : 'status-inactive'}>
                  {user.isActive ? 'פעיל' : 'לא פעיל'}
                </strong>
              </div>
            </div>
            
            <div className="user-actions">
              <button className="btn-edit" onClick={() => handleEdit(user)}>
                ערוך
              </button>
              <button 
                className={user.isActive ? 'btn-deactivate' : 'btn-activate'}
                onClick={() => handleToggleActive(user)}
              >
                {user.isActive ? 'השבת' : 'הפעל'}
              </button>
              <button className="btn-delete" onClick={() => handleDelete(user._id)}>
                מחק
              </button>
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && (
        <div className="no-data">אין משתמשים במערכת</div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingUser ? 'עריכת משתמש' : 'משתמש חדש'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>שם משתמש (לכניסה)</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={editingUser}
                  placeholder="לדוגמה: yaki"
                />
              </div>
              
              <div className="form-group">
                <label>{editingUser ? 'סיסמה חדשה (השאר ריק לשמור הקיימת)' : 'סיסמה'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder="******"
                />
              </div>
              
              <div className="form-group">
                <label>שם מלא</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="לדוגמה: יקי אלהרר"
                />
              </div>
              
              <div className="form-group">
                <label>תפקיד</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="admin">מנהל מערכת - גישה מלאה</option>
                  <option value="editor">עורך - עריכת BOMs ופריטים</option>
                  <option value="sales">איש מכירות - מחשבונים בלבד</option>
                </select>
              </div>
              
              {formData.role === 'sales' && (
                <div className="form-group">
                  <label>מחירון מינימלי</label>
                  <select
                    value={formData.minPriceList}
                    onChange={(e) => setFormData({ ...formData, minPriceList: parseInt(e.target.value) })}
                  >
                    {priceLists.map(pl => (
                      <option key={pl._id} value={pl.level}>
                        {pl.name} ({pl.marginPercent}%)
                      </option>
                    ))}
                  </select>
                  <p className="form-hint">
                    איש המכירות לא יוכל לבחור מחירון נמוך יותר מזה
                  </p>
                </div>
              )}
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  משתמש פעיל
                </label>
              </div>
              
              <div className="modal-buttons">
                <button type="submit" className="btn-primary">שמור</button>
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>ביטול</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;